import type { WorkflowConfig, WorkflowStepConfig } from '@kfolks/types'

import type { ValidationError, ValidationResult } from './form-schema-validator.js'

/**
 * Cross-step graph validator for the canonical linear step-array
 * `WorkflowConfig` shape (ADR-0012). Supplements `workflowConfigSchema` (Zod
 * wire-shape validator); does NOT replicate per-step shape checks.
 *
 * Consumed by:
 *   - Backend `RequestTypeService.create` / `.update` on save (closes Spec 5
 *     §5.5's "ensures it actually does" gap for workflow configs).
 *   - The new `POST /requests/types/:id/workflow/validate` endpoint.
 *   - The frontend visual workflow builder for inline / debounced feedback.
 *
 * Single source of truth — FE and BE share this function so inline errors
 * and on-save errors match byte-for-byte.
 *
 * Reachability semantics (matters for callers who want to predict engine
 * behavior): the engine walks `steps[]` top-to-bottom. When a step has no
 * `after`, control flows to the next array index. When `after.next` is set,
 * control jumps there. Terminal markers ('completed' | 'rejected' |
 * 'cancelled') end the workflow. So a step is reachable if:
 *   - it is `steps[0]`, OR
 *   - some preceding step's implicit successor (no `after`) lands on it, OR
 *   - some step's `after.next` names its id.
 *
 * The graph is dependency-free — a hand-rolled BFS keeps `@kfolks/models`
 * lean (consumed by both backend and frontend). `@dagrejs/graphlib` stays
 * a frontend-only dep for canvas layout, not for this validator.
 */

const TERMINAL_MARKERS = new Set<string>(['completed', 'rejected', 'cancelled'])

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function validateWorkflowConfig(config: WorkflowConfig | unknown): ValidationResult {
	const errors: ValidationError[] = []

	if (!isRecord(config)) {
		errors.push({
			path: '',
			message: 'workflowConfig must be an object with shape { version, steps }',
			severity: 'error',
		})
		return { valid: false, errors }
	}

	if (config.version !== '1.0') {
		errors.push({ path: 'version', message: 'Only version "1.0" is supported in Phase 1', severity: 'error' })
	}

	if (!Array.isArray(config.steps)) {
		errors.push({ path: 'steps', message: 'steps must be an array', severity: 'error' })
		return { valid: false, errors }
	}

	const steps = config.steps as WorkflowStepConfig[]
	if (steps.length === 0) {
		errors.push({ path: 'steps', message: 'workflow must declare at least one step', severity: 'error' })
		return { valid: false, errors }
	}

	const stepIds = new Set<string>()
	for (let i = 0; i < steps.length; i++) {
		const step = steps[i]
		if (!isRecord(step) || typeof step.id !== 'string') {
			// Zod handles these; if we got here without Zod we still want a graceful early return.
			errors.push({ path: `steps[${i}].id`, message: 'step must have a string id', severity: 'error' })
			continue
		}
		stepIds.add(step.id)
	}

	// 1. `after.next` must resolve to a known step id or a terminal marker.
	for (let i = 0; i < steps.length; i++) {
		const step = steps[i]
		if (!isRecord(step)) continue
		const after = step.after as { next?: unknown } | undefined
		if (!after) continue
		if (typeof after.next !== 'string') continue
		const target = after.next
		if (!TERMINAL_MARKERS.has(target) && !stepIds.has(target)) {
			errors.push({
				path: `steps[${i}].after.next`,
				message: `unknown transition target "${target}"`,
				severity: 'error',
			})
		}
	}

	// 2. Approver-dimension presence on approval steps.
	//    Parity with `workflowConfigSchema.superRefine` so FE callers see one merged list.
	for (let i = 0; i < steps.length; i++) {
		const step = steps[i]
		if (!isRecord(step)) continue
		if (step.type !== 'approval') continue
		const hasSpecificPeople = Array.isArray(step.specificPeople) && step.specificPeople.length > 0
		const hasGroups = Array.isArray(step.groups) && step.groups.length > 0
		const hasSubmittersManager = step.submittersManager === true
		const hasCollaboratorsManager = step.collaboratorsManager === true
		if (!hasSpecificPeople && !hasGroups && !hasSubmittersManager && !hasCollaboratorsManager) {
			errors.push({
				path: `steps[${i}]`,
				message:
					'approval step requires at least one approver dimension (specificPeople, groups, submittersManager, or collaboratorsManager)',
				severity: 'error',
			})
		}
	}

	// 3. Reachability — BFS from steps[0]. A step `s` is reachable when:
	//      - it is steps[0], OR
	//      - its predecessor in array order has no `after` (implicit fall-through), OR
	//      - some step's `after.next` names s.id.
	//    Terminal-marker `after.next` exits the workflow at that step (no successor).
	const reachable = new Set<string>()
	const visited = new Set<string>()
	const queue: string[] = []
	const firstStepId = isRecord(steps[0]) && typeof steps[0].id === 'string' ? steps[0].id : null
	if (firstStepId) {
		queue.push(firstStepId)
		reachable.add(firstStepId)
	}

	const stepIndexById = new Map<string, number>()
	for (let i = 0; i < steps.length; i++) {
		const step = steps[i]
		if (isRecord(step) && typeof step.id === 'string') stepIndexById.set(step.id, i)
	}

	while (queue.length > 0) {
		const id = queue.shift()
		if (!id || visited.has(id)) continue
		visited.add(id)
		const idx = stepIndexById.get(id)
		if (idx === undefined) continue
		const step = steps[idx]
		if (!isRecord(step)) continue
		const after = step.after as { next?: unknown } | undefined

		if (after && typeof after.next === 'string') {
			const target = after.next
			if (TERMINAL_MARKERS.has(target)) continue // workflow terminates here
			if (stepIndexById.has(target) && !visited.has(target)) {
				reachable.add(target)
				queue.push(target)
			}
			continue
		}

		// No `after` — implicit fall-through to the next array index.
		const nextIdx = idx + 1
		if (nextIdx < steps.length) {
			const nextStep = steps[nextIdx]
			if (isRecord(nextStep) && typeof nextStep.id === 'string' && !visited.has(nextStep.id)) {
				reachable.add(nextStep.id)
				queue.push(nextStep.id)
			}
		}
		// else: array exhausted — implicit termination.
	}

	for (let i = 0; i < steps.length; i++) {
		const step = steps[i]
		if (!isRecord(step) || typeof step.id !== 'string') continue
		if (!reachable.has(step.id)) {
			errors.push({
				path: `steps[${i}]`,
				message: `step "${step.id}" is unreachable from steps[0]`,
				severity: 'error',
			})
		}
	}

	// 4. At least one path must reach a terminal — explicit ('completed' / 'rejected' /
	//    'cancelled' as `after.next`) or implicit (last step in array order, with no `after`).
	let canTerminate = false
	for (const id of visited) {
		const idx = stepIndexById.get(id)
		if (idx === undefined) continue
		const step = steps[idx]
		if (!isRecord(step)) continue
		const after = step.after as { next?: unknown } | undefined
		if (after && typeof after.next === 'string' && TERMINAL_MARKERS.has(after.next)) {
			canTerminate = true
			break
		}
		// Implicit termination: no `after` and this is the last step in array order.
		if (!after && idx === steps.length - 1) {
			canTerminate = true
			break
		}
	}
	if (!canTerminate) {
		errors.push({
			path: 'steps',
			message: 'workflow has no path to a terminal marker (completed | rejected | cancelled) or implicit end',
			severity: 'error',
		})
	}

	return { valid: errors.length === 0, errors }
}
